import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Music, Guitar, Type, Minus, Plus, Printer, ChevronLeft, ChevronRight, Key, User, Copy, Check, Calendar, Disc, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SECTION_LABELS = ['verse', 'chorus', 'bridge', 'pre-chorus', 'prechorus', 'pre chorus',
  'tag', 'outro', 'intro', 'interlude', 'refrain', 'coda', 'vamp', 'ending', 'instrumental', 'repeat'];

function isSectionLabel(line) {
  const s = line.trim();
  if (/^\[.+\]$/.test(s) || /^\(.+\)$/.test(s)) return true;
  const lower = s.toLowerCase().replace(/:$/, '').trim();
  return SECTION_LABELS.some(lbl => lower === lbl || new RegExp(`^${lbl}\\s*\\d*$`).test(lower));
}

function formatSectionLabel(line) {
  return line.trim().replace(/^\[|\]$|\(|\)/g, '').replace(/:$/, '').trim();
}

function isChordLine(line) {
  const s = line.trim();
  if (!s) return false;
  // Check for pipe-delimited chord lines like "| E | | A2(no3) | |"
  if (/^\|[\s|A-Ga-g#b0-9\(\)\/mMajindisugadNno]+\|$/.test(s)) return true;
  // Remove parenthetical instructions before checking
  const cleaned = s.replace(/\(no\s*\d+\)/gi, '').replace(/\([^)]*\)/g, '');
  const tokens = cleaned.trim().split(/\s+/).filter(t => t.length > 0);
  if (!tokens.length) return s.length < 30; // Short parenthetical-only lines
  const chordRe = /^[A-G][#b]?(m|maj|min|dim|aug|sus|add|M)?[0-9]?[0-9]?(sus[24]?)?(add[0-9]+)?(\/[A-G][#b]?)?$/i;
  const chordCount = tokens.filter(t => chordRe.test(t.replace(/[()]/g, ''))).length;
  return chordCount > 0 && (chordCount / tokens.length) >= 0.5;
}

const LyricsRenderer = ({ content, fontSize }) => {
  if (!content) return null;
  const lines = content.split('\n');
  const elements = [];
  let stanzaLines = [];

  const flushStanza = (key) => {
    if (!stanzaLines.length) return;
    elements.push(
      <div key={key} className="mb-5">
        {stanzaLines.map((l, i) => <div key={i} className="text-stone-800 leading-relaxed">{l || '\u00A0'}</div>)}
      </div>
    );
    stanzaLines = [];
  };

  lines.forEach((line, i) => {
    const stripped = line.trim();

    if (isSectionLabel(stripped)) {
      flushStanza(`stanza-${i}`);
      elements.push(
        <div key={`section-${i}`} className="flex items-center gap-3 mt-6 mb-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
            {formatSectionLabel(stripped)}
          </span>
          <div className="flex-1 h-px bg-stone-100"></div>
        </div>
      );
      return;
    }

    // Skip chord-only lines that leaked into lyrics
    if (isChordLine(stripped)) return;

    if (!stripped) {
      flushStanza(`stanza-${i}`);
      return;
    }

    stanzaLines.push(stripped);
  });

  flushStanza('last');
  return <div style={{ fontSize }}>{elements}</div>;
};

const ChordsRenderer = ({ content, fontSize }) => {
  if (!content) return null;
  const lines = content.split('\n');

  return (
    <pre className="font-mono whitespace-pre-wrap" style={{ fontSize }}>
      {lines.map((line, i) => {
        const stripped = line.trim();

        if (isSectionLabel(stripped)) {
          return (
            <div key={i} className="flex items-center gap-3 mt-6 mb-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600 bg-amber-50 px-3 py-1 rounded-full font-sans">
                {formatSectionLabel(stripped)}
              </span>
              <div className="flex-1 h-px bg-stone-100"></div>
            </div>
          );
        }

        if (isChordLine(stripped)) {
          return <div key={i} className="text-amber-600 font-bold tracking-wider select-all" style={{ fontSize: fontSize * 0.85 }}>{line}</div>;
        }

        if (!stripped) return <div key={i}>{'\u00A0'}</div>;

        return <div key={i} className="text-stone-800">{line}</div>;
      })}
    </pre>
  );
};

const SongDetailPage = () => {
  const { language, songId } = useParams();
  const navigate = useNavigate();
  const langPath = (language || 'english').toLowerCase();
  const isTelugu = langPath === 'telugu';
  const isHindi = langPath === 'hindi';
  const isIndian = isTelugu || isHindi;
  const nativeStyle = isTelugu ? {fontFamily: "'Noto Sans Telugu', sans-serif"} : {};
  const [song, setSong] = useState(null);
  const [allSongs, setAllSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('lyrics');
  const [fontSize, setFontSize] = useState(18);
  const [copied, setCopied] = useState(false);
  const lyricsRef = useRef(null);

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/songs/${songId}`).then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); }),
      fetch(`${BACKEND_URL}/api/songs?language=${isTelugu ? 'Telugu' : 'English'}`).then(r => r.json()).then(d => d.songs || d)
    ]).then(([songData, songsData]) => {
      setSong(songData);
      setAllSongs(songsData);
      if (songData.lyrics_with_chords && !songData.lyrics) setViewMode('chords');
    }).catch(() => setSong(null)).finally(() => setLoading(false));
  }, [songId]);

  const currentIndex = allSongs.findIndex(s => s.id === songId);
  const prevSong = currentIndex > 0 ? allSongs[currentIndex - 1] : null;
  const nextSong = currentIndex < allSongs.length - 1 ? allSongs[currentIndex + 1] : null;

  const handlePrint = useCallback(() => window.print(), []);

  const handleCopy = useCallback(() => {
    const text = viewMode === 'chords' && song?.lyrics_with_chords
      ? song.lyrics_with_chords : song?.lyrics || '';
    navigator.clipboard.writeText(`${song.title}\n\n${text}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [song, viewMode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20" data-testid="song-detail-loading">
        <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20" data-testid="song-not-found">
        <div className="text-center px-4">
          <Music className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Song Not Found</h2>
          <button onClick={() => navigate(`/songs/${langPath}`)} className="mt-4 bg-amber-600 text-white px-6 py-3 rounded-lg hover:bg-amber-700 transition-colors font-medium" data-testid="back-to-library-btn">
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  const hasChords = !!song.lyrics_with_chords;
  const isTransliteration = isIndian && hasChords;
  const displayContent = viewMode === 'chords' && hasChords ? song.lyrics_with_chords : song.lyrics;
  const metaItems = [
    song.author && { icon: User, label: 'Songwriter', value: song.author },
    song.recording_artist && { icon: Disc, label: 'Artist', value: song.recording_artist },
    song.year && { icon: Calendar, label: 'Year', value: song.year },
    song.label && { icon: Tag, label: 'Label', value: song.label },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-stone-50 pt-16" data-testid="song-detail-page">
      {/* Sticky Toolbar */}
      <div className="bg-white border-b border-stone-200 sticky top-16 z-40 print:hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-2">
          <button data-testid="back-to-songs-btn" onClick={() => navigate(`/songs/${langPath}`)} className="flex items-center gap-1.5 text-stone-600 hover:text-amber-600 transition-colors font-medium text-sm">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Library</span>
          </button>

          <div className="flex items-center gap-1.5">
            {hasChords && (
              <div className="flex bg-stone-100 rounded-lg p-0.5" data-testid="view-mode-toggle">
                <button data-testid="lyrics-view-btn" onClick={() => setViewMode('lyrics')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'lyrics' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-500 hover:text-stone-700'}`}>
                  <Type className="w-3.5 h-3.5" /> {isTransliteration ? 'Original' : 'Lyrics'}
                </button>
                <button data-testid="chords-view-btn" onClick={() => setViewMode('chords')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'chords' ? 'bg-white shadow-sm text-stone-800' : 'text-stone-500 hover:text-stone-700'}`}>
                  <Guitar className="w-3.5 h-3.5" /> {isTransliteration ? 'English' : 'Chords'}
                </button>
              </div>
            )}

            <div className="flex items-center bg-stone-100 rounded-lg">
              <button data-testid="font-decrease-btn" onClick={() => setFontSize(prev => Math.max(12, prev - 2))} className="p-1.5 text-stone-500 hover:text-stone-700 transition-colors">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs text-stone-500 font-mono w-6 text-center">{fontSize}</span>
              <button data-testid="font-increase-btn" onClick={() => setFontSize(prev => Math.min(32, prev + 2))} className="p-1.5 text-stone-500 hover:text-stone-700 transition-colors">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <button data-testid="copy-btn" onClick={handleCopy} className="p-2 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-all" title="Copy lyrics">
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            </button>
            <button data-testid="print-btn" onClick={handlePrint} className="p-2 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-lg transition-all" title="Print">
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Song Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden" data-testid="song-content-card">
          {/* Song Header */}
          <div className="px-6 sm:px-10 pt-8 pb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 leading-tight mb-1" data-testid="song-title" style={nativeStyle}>
              {song.title}
            </h1>
            {isIndian && song.english_title && (
              <p className="text-base text-stone-400 mb-4" data-testid="song-english-title">{song.english_title}</p>
            )}
            {!isTelugu && <div className="mb-4" />}

            {/* Metadata Grid */}
            {metaItems.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4" data-testid="song-metadata">
                {metaItems.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-2.5 text-sm">
                    <Icon className="w-4 h-4 text-stone-400 flex-shrink-0" />
                    <span className="text-stone-400 font-medium">{label}:</span>
                    <span className="text-stone-700 truncate" data-testid={`song-meta-${label.toLowerCase()}`}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Key + Themes Row */}
            <div className="flex flex-wrap items-center gap-2">
              {song.key_signature && (
                <span className="text-xs text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full font-semibold flex items-center gap-1" data-testid="song-key">
                  <Key className="w-3 h-3" /> Key of {song.key_signature}
                </span>
              )}
              {song.themes && song.themes.length > 0 && song.themes.map(theme => (
                <span key={theme} className="text-xs text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full font-medium">
                  {theme}
                </span>
              ))}
            </div>
          </div>

          {/* Divider between metadata and lyrics */}
          <div className="border-t border-stone-200 mx-6 sm:mx-10"></div>

          {/* Lyrics/Chords Display */}
          <div className="px-6 sm:px-10 py-8" ref={lyricsRef} data-testid="song-lyrics-content" style={nativeStyle}>
            {displayContent ? (
              viewMode === 'chords' && hasChords
                ? <ChordsRenderer content={displayContent} fontSize={fontSize} />
                : <LyricsRenderer content={displayContent} fontSize={fontSize} />
            ) : (
              <div className="text-center py-12" data-testid="no-lyrics">
                <Music className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500">Lyrics are not yet available for this song.</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 print:hidden" data-testid="song-navigation">
          {prevSong ? (
            <button data-testid="prev-song-btn" onClick={() => navigate(`/songs/${langPath}/${prevSong.id}`)} className="flex items-center gap-2 text-stone-600 hover:text-amber-600 transition-colors group">
              <ChevronLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
              <div className="text-left">
                <div className="text-xs text-stone-400">Previous</div>
                <div className="text-sm font-medium truncate max-w-[150px] sm:max-w-[250px]">{prevSong.title}</div>
              </div>
            </button>
          ) : <div />}
          {nextSong ? (
            <button data-testid="next-song-btn" onClick={() => navigate(`/songs/${langPath}/${nextSong.id}`)} className="flex items-center gap-2 text-stone-600 hover:text-amber-600 transition-colors group text-right">
              <div>
                <div className="text-xs text-stone-400">Next</div>
                <div className="text-sm font-medium truncate max-w-[150px] sm:max-w-[250px]">{nextSong.title}</div>
              </div>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </button>
          ) : <div />}
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="song-content-card"], [data-testid="song-content-card"] * { visibility: visible; }
          [data-testid="song-content-card"] { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none; border: none; }
        }
      `}</style>
    </div>
  );
};

export default SongDetailPage;
